import { PACKAGE_JSON_FILENAME } from "@/constants";
import { useActiveChatId } from "@/lib/hooks/useActiveChatId";
import {
  selectVersionInformationFromState,
  useLatestVersionNumber,
} from "@/lib/hooks/useVersionInformation";
import { useMessageStore } from "@/state/stores";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

export function useNavigateToLatest(enabled = true) {
  // Get base information like chatId, latest version number and get the navigate function
  const chatId = useActiveChatId();
  const latestVersionNumber = useLatestVersionNumber();
  const navigate = useNavigate();

  // Get the latest spec id from store
  const latestSpecId = useMessageStore(
    (state) =>
      selectVersionInformationFromState(state, latestVersionNumber)?.spec?.id,
  );

  // Get the latest filename from store
  const latestFilename = useMessageStore((state) => {
    const filteredFiles = selectVersionInformationFromState(
      state,
      latestVersionNumber,
    )?.files.filter(
      (file) =>
        !file.filename.startsWith("Deploy Status:") &&
        file.filename !== PACKAGE_JSON_FILENAME,
    );
    if (!filteredFiles) {
      return;
    }

    return filteredFiles[filteredFiles.length - 1]?.filename;
  });

  const specIdRef = useRef<string | undefined>(latestSpecId);
  const latestFilenameRef = useRef<string | undefined>(latestFilename);

  // Navigate to the appropriate view based on which value changed
  useEffect(() => {
    if (!chatId || !enabled) {
      return;
    }

    const specIdChanged = specIdRef.current !== latestSpecId;
    const filenameChanged = latestFilenameRef.current !== latestFilename;

    // Update refs
    specIdRef.current = latestSpecId;
    latestFilenameRef.current = latestFilename;

    // Navigate based on what changed, prioritizing spec
    if (specIdChanged && latestSpecId) {
      navigate({
        to: "/codegen/$chatId/attachments/$versionNumber",
        params: {
          chatId,
          versionNumber: `v${latestVersionNumber}`,
        },
        search: { tab: "spec" },
      });
    } else if (filenameChanged && latestFilename) {
      navigate({
        to: "/codegen/$chatId/attachments/$versionNumber",
        params: {
          chatId,
          versionNumber: `v${latestVersionNumber}`,
        },
        search: { tab: "code", file: latestFilename },
      });
    }
  }, [
    latestSpecId,
    latestFilename,
    chatId,
    latestVersionNumber,
    navigate,
    enabled,
  ]);
}
